import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, FileText, Building2,
  Settings, LogOut, ChevronRight, Menu, X, Bell,
  UserCircle, PlusCircle, BarChart3, Archive, BookOpen, Plug, Receipt, MapPin, TrendingUp
} from 'lucide-react';

const navItems = [
  { path: '/dashboard-agenzia', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/necrologi/crea', label: 'Nuovo Necrologio', icon: PlusCircle },
  { path: '/dashboard-agenzia?section=necrologi', label: 'Gestione Necrologi', icon: FileText },
  { path: '/agenzia/profilo', label: 'Profilo Agenzia', icon: Building2 },
  { path: '/dashboard-agenzia?section=sedi', label: 'Sedi e Filiali', icon: MapPin },
  { path: '/dashboard-agenzia?section=fatturazione', label: 'Dati Fatturazione', icon: Receipt },
  { path: '/dashboard-agenzia?section=statistiche-avanzate', label: 'Statistiche Avanzate', icon: TrendingUp },
  { path: '/agenzia/manifesti-ricordini', label: 'Manifesti', icon: FileText },
  { path: '/agenzia/libro-memoria', label: 'Libro della Memoria', icon: BookOpen },
  { path: '/dashboard-agenzia?section=area-famiglia', label: 'Area Famiglia', icon: UserCircle },
  { path: '/dashboard-agenzia?section=fatture', label: 'Fatture', icon: Receipt },
  { path: '/dashboard-agenzia?section=contratti', label: 'I miei Contratti', icon: FileText },
  { path: '/agenzia/archivio-contratti', label: 'Archivio Documenti', icon: Archive },
  { path: '/agenzia/api-keys', label: 'Integrazione API', icon: Plug },
  { path: '/agenzia/impostazioni', label: 'Impostazioni', icon: Settings },
];

const AgencyLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getBreadcrumbs = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const crumbs = [{ label: 'Dashboard', path: '/dashboard-agenzia' }];
    const currentNav = navItems.find(item => location.pathname.startsWith(item.path));
    if (currentNav && currentNav.path !== '/dashboard-agenzia') {
      crumbs.push({ label: currentNav.label, path: currentNav.path });
    }
    return crumbs;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="flex h-screen bg-agency-background" data-testid="agency-layout">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-agency-sidebar transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } flex flex-col`}
        data-testid="agency-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <Link to="/dashboard-agenzia" className="flex items-center gap-3" data-testid="agency-sidebar-logo">
            <div className="w-8 h-8 rounded-lg bg-agency-secondary flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-display text-white text-lg tracking-tight">Gestionale</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Agency Profile Summary */}
        <div className="px-4 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-agency-primary flex items-center justify-center text-white font-bold text-sm">
              {(user?.agenzia_nome || user?.nome || 'A').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {user?.agenzia_nome || user?.nome || 'Agenzia'}
              </p>
              <p className="text-slate-400 text-xs truncate">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            // Estrai eventuale ?section=... per matching attivo accurato
            const [itemPathname, itemQuery = ''] = item.path.split('?');
            const itemSection = new URLSearchParams(itemQuery).get('section');
            const currentSection = new URLSearchParams(location.search).get('section');
            let isActive = false;
            if (itemSection) {
              // Voce con query param: deve coincidere pathname + section
              isActive = location.pathname === itemPathname && currentSection === itemSection;
            } else if (item.path === '/dashboard-agenzia') {
              // Voce Dashboard "pulita": attiva solo se siamo su /dashboard-agenzia SENZA ?section
              isActive = location.pathname === '/dashboard-agenzia' && !currentSection;
            } else {
              isActive = location.pathname === item.path ||
                location.pathname.startsWith(item.path + '/');
            }
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`agency-sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-white/5 transition-colors duration-200 w-full"
            data-testid="agency-sidebar-logout"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            Esci
          </button>
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors duration-200 mt-1"
            data-testid="agency-sidebar-home"
          >
            <ChevronRight className="w-5 h-5 rotate-180" strokeWidth={1.5} />
            Torna al Sito
          </Link>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-agency-accent flex items-center px-6 gap-4" data-testid="agency-header">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-slate-600 hover:text-agency-primary transition-colors"
            data-testid="agency-header-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm" data-testid="agency-breadcrumbs">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={crumb.path}>
                {idx > 0 && <ChevronRight className="w-4 h-4 text-slate-300" />}
                {idx === breadcrumbs.length - 1 ? (
                  <span className="text-agency-primary font-medium">{crumb.label}</span>
                ) : (
                  <Link to={crumb.path} className="text-slate-500 hover:text-agency-primary transition-colors">
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* Right side */}
          <div className="ml-auto flex items-center gap-3">
            <button
              className="relative p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid="agency-header-notifications-btn"
            >
              <Bell className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <div className="w-8 h-8 rounded-full bg-agency-primary flex items-center justify-center text-white text-xs font-bold">
                {(user?.nome || 'A').charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-slate-700 hidden sm:block">
                {user?.nome || 'Utente'}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AgencyLayout;
